import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Send, CheckCircle2, XCircle, Eye, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CampaignDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchCampaign();
  }, [id]);

  const fetchCampaign = async () => {
    try {
      const response = await axios.get(`${API_URL}/campaigns/${id}`);
      setCampaign(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement de la campagne');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCampaign = async () => {
    setSending(true);
    try {
      await axios.post(`${API_URL}/campaigns/${id}/send`);
      toast.success('Campagne envoyée avec succès!');
      fetchCampaign();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi de la campagne');
    } finally {
      setSending(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case 'read':
        return <Eye className="h-4 w-4 text-blue-600" />;
      case 'sent':
        return <Send className="h-4 w-4 text-indigo-600" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      default:
        return <XCircle className="h-4 w-4 text-red-600" />;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64" data-testid="loading">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-700"></div>
        </div>
      </Layout>
    );
  }

  if (!campaign) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-slate-500">Campagne introuvable</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-8" data-testid="campaign-detail-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/campaigns')}
              className="rounded-lg"
              data-testid="back-button"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {campaign.name}
              </h1>
              <p className="mt-1 text-slate-600">
                Créé le {new Date(campaign.created_at).toLocaleDateString('fr-FR')}
              </p>
            </div>
          </div>
          {campaign.status === 'draft' && (
            <Button
              onClick={handleSendCampaign}
              disabled={sending}
              className="rounded-full px-6 h-12 bg-indigo-700 font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
              data-testid="send-campaign-button"
            >
              <Send className="mr-2 h-5 w-5" />
              {sending ? 'Envoi...' : 'Envoyer la campagne'}
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-6">
              <p className="text-sm font-medium text-slate-600">Total destinataires</p>
              <p className="mt-2 text-3xl font-bold text-slate-900" data-testid="total-recipients">
                {campaign.total_recipients}
              </p>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-6">
              <p className="text-sm font-medium text-slate-600">Envoyés</p>
              <p className="mt-2 text-3xl font-bold text-blue-600" data-testid="sent-count">
                {campaign.sent_count}
              </p>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-6">
              <p className="text-sm font-medium text-slate-600">Livrés</p>
              <p className="mt-2 text-3xl font-bold text-green-600" data-testid="delivered-count">
                {campaign.delivered_count}
              </p>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-6">
              <p className="text-sm font-medium text-slate-600">Lus</p>
              <p className="mt-2 text-3xl font-bold text-indigo-600" data-testid="read-count">
                {campaign.read_count}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Message Content */}
        <Card className="rounded-2xl border-slate-100">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Contenu du message</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg bg-slate-50 p-6">
              <p className="text-sm font-medium text-slate-500 mb-2">Type: {campaign.message_type}</p>
              <p className="text-slate-900 whitespace-pre-wrap">{campaign.content.text || JSON.stringify(campaign.content, null, 2)}</p>
            </div>
          </CardContent>
        </Card>

        {/* Messages List */}
        {campaign.messages && campaign.messages.length > 0 && (
          <Card className="rounded-2xl border-slate-100">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Détail des messages</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2" data-testid="messages-list">
                {campaign.messages.map((message) => (
                  <div
                    key={message.id}
                    className="flex items-center justify-between rounded-lg border border-slate-100 p-3"
                    data-testid={`message-${message.id}`}
                  >
                    <div className="flex items-center space-x-3">
                      {getStatusIcon(message.status)}
                      <div>
                        <p className="font-medium text-slate-900">{message.recipient_phone}</p>
                        {message.delivered_at && (
                          <p className="text-xs text-slate-500">
                            Livré le {new Date(message.delivered_at).toLocaleString('fr-FR')}
                          </p>
                        )}
                      </div>
                    </div>
                    <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                      message.status === 'delivered' ? 'bg-green-50 text-green-700' :
                      message.status === 'read' ? 'bg-blue-50 text-blue-700' :
                      message.status === 'sent' ? 'bg-indigo-50 text-indigo-700' :
                      message.status === 'pending' ? 'bg-yellow-50 text-yellow-700' :
                      'bg-red-50 text-red-700'
                    }`}>
                      {message.status}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default CampaignDetail;